import { Utils } from '.'
import { Objects } from '../object-definitions'
import { arrayToMap, assertUnreachable } from './others'

type QaKey = keyof Objects.MailingOrder.QAAttributes

const qaFields: QaKey[] = ['arePhotosReviewed', 'areVariablesReviewed', 'isLayoutReviewed', 'isLogoReviewed', 'needsClientFix', 'qaComment', 'isQRScanned']

export namespace MailingOrder {
    export function getFirstMailingListsConfigMappingAsMap(mailingOrder: Objects.MailingOrder.Parse) {
        return arrayToMap(mailingOrder?.get('mailingListsConfig')?.[0]?.mapping ?? [], (x) => x.listHeader)
    }

    export function getDefaultPostageClass(productCode: Objects.Product.Code.Any): Objects.MailingOrder.IFields.PostageClass.Value {
        switch (productCode) {
            case 'BROCHURE':
                return 'STANDARD'
            case 'FLYER':
                return 'PRINT AND SHIP'
            case 'GREETING-CARD':
                return 'FIRST'
            case 'LETTER':
                return 'STANDARD'
            case 'POSTCARD':
                return 'STANDARD'
            case 'BROCHURE:PRINT-AND-SHIP-ONLY':
                return 'PRINT AND SHIP'
            case 'BUSINESS-CARD:HORIZONTAL':
            case 'BUSINESS-CARD:VERTICAL':
                return 'PRINT AND SHIP'
            case 'DOOR-HANGER':
                return 'PRINT AND SHIP'
            case 'TRI-FOLD-BROCHURE':
                return 'PRINT AND SHIP'
            case 'SIGN:RIDER':
            case 'SIGN:YARD':
            case 'SIGN:HORIZONTAL':
            case 'SIGN:VERTICAL':
                return 'PRINT AND SHIP'
            case 'GREETING-CARD:HORIZONTAL':
            case 'GREETING-CARD:VERTICAL':
                return 'STANDARD'
            case undefined:
                return 'STANDARD'
        }

        assertUnreachable(productCode)
    }

    // TODO add Greeting cards
    // TODO Add Postcards Property
    // TODO PC & Brochure paper 70 check
    export function getProductCodePaperPrintCoatingCompatibility(
        productCode: Objects.Product.Code.Any,
        paper: Objects.Mailing.IFields.Paper,
        printCoating: Objects.Mailing.IFields.PrintCoating
    ): { compatible: true } | { compatible: false; errorMessage: string } {
        let returnValue: { compatible: true } | { compatible: false; errorMessage: string }
        switch (productCode) {
            case 'BROCHURE':
            case 'BROCHURE:PRINT-AND-SHIP-ONLY':
                if (paper.basisWeight === 220) {
                    returnValue = { compatible: false, errorMessage: 'Super Thick paper option is not available for Brochures' }
                }

                if (printCoating === 'UV') {
                    returnValue = { compatible: false, errorMessage: 'UV coating option is not available for Brochures' }
                }
                returnValue = { compatible: true }
                break
            case 'LETTER':
                if (printCoating === 'UV') {
                    returnValue = { compatible: false, errorMessage: 'UV coating option is not available for Letters' }
                }

                if (paper.basisWeight === 220) {
                    returnValue = { compatible: false, errorMessage: 'Super Thick paper option is not available for Letters' }
                }

                if (paper.basisWeight === 100) {
                    returnValue = { compatible: false, errorMessage: 'Thick 100# paper option is not available for Letters' }
                }

                returnValue = { compatible: true }
                break
            case 'FLYER':
            case 'POSTCARD':
            case 'GREETING-CARD':
            case 'BUSINESS-CARD:HORIZONTAL':
            case 'BUSINESS-CARD:VERTICAL':
            case 'DOOR-HANGER':
            case 'TRI-FOLD-BROCHURE':
            case 'SIGN:RIDER':
            case 'SIGN:YARD':
            case 'SIGN:HORIZONTAL':
            case 'SIGN:VERTICAL':
                returnValue = { compatible: true }
                break
            case 'GREETING-CARD:HORIZONTAL':
            case 'GREETING-CARD:VERTICAL':
                if (printCoating === 'UV') {
                    returnValue = { compatible: false, errorMessage: 'UV coating option is not available for Greeting Cards' }
                }

                if (paper.basisWeight !== 100) {
                    returnValue = { compatible: false, errorMessage: 'Only 100# paper is available for Greeting Cards' }
                }

                returnValue = { compatible: true }
                break
            case undefined:
                returnValue = { compatible: true }
                break
        }
        return returnValue
    }

    export function isQaCheckListComplete(mailingOrder: Objects.MailingOrder.Parse) {
        if (MailingOrder.needsClientFix(mailingOrder)) {
            return false
        }

        if (!isSignChecklistComplete(mailingOrder)) {
            return false
        }

        if (mailingOrder.get('isSample')) {
            return true
        }

        if (mailingOrder.get('postageClass') === 'STANDARD' || mailingOrder.get('postageClass') === 'FIRST') {
            if ((mailingOrder.get('records') ?? 0) < 10) {
                return true
            }
        }

        return (
            mailingOrder.get('isLogoReviewed') &&
            mailingOrder.get('isLayoutReviewed') &&
            mailingOrder.get('arePhotosReviewed') &&
            mailingOrder.get('areVariablesReviewed')
        )
    }
    export function needsClientFix(mailingOrder: Objects.MailingOrder.Parse) {
        return mailingOrder.get('needsClientFix')
    }

    export function isQRScanned(mailingOrder: Objects.MailingOrder.Parse) {
        return mailingOrder.get('isQRScanned')
    }

    export function isSignChecklistComplete(mailingOrder: Objects.MailingOrder.Parse) {
        return mailingOrder.get('productType') !== 'SIGN' || MailingOrder.isQRScanned(mailingOrder)
    }

    function isQaField(key: keyof Objects.MailingOrder.Attributes): key is keyof Objects.MailingOrder.QAAttributes {
        const unstrictQAFields: string[] = qaFields
        return unstrictQAFields.includes(key)
    }

    export function getQaAdminChangesDictionary(adminChanges: Objects.MailingOrder.Attributes['adminChanges']) {
        if (!adminChanges?.length) {
            return undefined
        }
        const dictionary: Partial<Record<QaKey, Objects.MailingOrder.IFields.AdminChanges.Item>> = {}
        for (const change of adminChanges) {
            const field = change.field
            if (!isQaField(field)) {
                continue
            }
            const previous = dictionary[field]
            if (!previous?.modifiedAt || (change.modifiedAt && change.modifiedAt > previous.modifiedAt)) {
                dictionary[field] = change
            }
        }
        return dictionary
    }

    export function getFulfillmentTypes(mailingOrder: Objects.MailingOrder.Parse): ('In House' | 'Zen Direct' | 'Xpressdocs')[] {
        // If updated result should be sorted alphabetically

        const productCode = Utils.Product.getProductCode(mailingOrder.get('productType'), mailingOrder.get('productTypeVariant'))

        switch (productCode) {
            case 'BROCHURE':
            case 'BROCHURE:PRINT-AND-SHIP-ONLY':
            case 'BUSINESS-CARD:HORIZONTAL':
            case 'BUSINESS-CARD:VERTICAL':
            case 'DOOR-HANGER':
            case 'FLYER':
            case 'GREETING-CARD:HORIZONTAL':
            case 'GREETING-CARD:VERTICAL':
            case 'LETTER':
            case 'POSTCARD':
            case 'SIGN:HORIZONTAL':
            case 'SIGN:RIDER':
            case 'SIGN:VERTICAL':
            case 'SIGN:YARD':
            case 'TRI-FOLD-BROCHURE':
                return ['In House']
            case undefined:
            case 'GREETING-CARD':
                return []
        }

        assertUnreachable(productCode)
        return []
    }

    export function getPrintAndShipBaseUnitPrice(
        quantity: number,
        code: Exclude<Objects.Product.Code.Any, Objects.Product.Type.GreetingCard | Objects.Product.Type.Letter>
    ) {
        const shipping: Objects.Establishment.Price.PrintAndShip.Shipping.Cents = 5000
        switch (code) {
            case 'POSTCARD': {
                const productUnitPrice: Objects.Establishment.Price.PrintAndShip.Postcard.Cents = 36
                return Math.min(Math.floor((shipping + quantity * productUnitPrice) / quantity), shipping)
            }
            case 'GREETING-CARD:HORIZONTAL':
            case 'GREETING-CARD:VERTICAL': {
                const productUnitPrice = getPrintAndShipGreetingCardsBaseUnitPrice(quantity)
                return Math.min(Math.floor((shipping + quantity * productUnitPrice) / quantity), shipping)
            }
            case 'BROCHURE':
            case 'BROCHURE:PRINT-AND-SHIP-ONLY': {
                const productUnitPrice: Objects.Establishment.Price.PrintAndShip.Brochure.Cents = 125
                return Math.min(Math.floor((shipping + quantity * productUnitPrice) / quantity), shipping)
            }
            case 'FLYER': {
                const productUnitPrice: Objects.Establishment.Price.PrintAndShip.Flyer.Cents = 63
                return Math.min(Math.floor((shipping + quantity * productUnitPrice) / quantity), shipping)
            }
            case 'BUSINESS-CARD:HORIZONTAL':
            case 'BUSINESS-CARD:VERTICAL': {
                const productUnitPrice: Objects.Establishment.Price.PrintAndShip.BusinessCard.Cents = 3
                const shipping: Objects.Establishment.Price.PrintAndShipBasePrice.BusinessCard.Cents = 3500
                return Math.min(Math.ceil((shipping + quantity * productUnitPrice) / quantity), shipping)
            }
            case 'DOOR-HANGER': {
                const productUnitPrice: Objects.Establishment.Price.PrintAndShip.DoorHanger.Cents = 30
                const shipping: Objects.Establishment.Price.PrintAndShipBasePrice.DoorHanger.Cents = 5500
                return Math.min(Math.ceil((shipping + quantity * productUnitPrice) / quantity), shipping)
            }
            case 'TRI-FOLD-BROCHURE': {
                const productUnitPrice: Objects.Establishment.Price.PrintAndShip.TriFoldBrochure.Cents = 69
                return Math.min(Math.floor((shipping + quantity * productUnitPrice) / quantity), shipping)
            }
            case 'SIGN:RIDER': {
                const productUnitPrice: Objects.Establishment.Price.PrintAndShip.SignRider.Cents = 2200
                const shipping: Objects.Establishment.Price.PrintAndShipBasePrice.SignRider.Cents = 2250
                if (quantity === 1) {
                    return singleSignItemPrice(productUnitPrice, shipping)
                }
                return Math.floor((shipping + quantity * productUnitPrice) / quantity)
            }
            case 'SIGN:VERTICAL': {
                const productUnitPrice: Objects.Establishment.Price.PrintAndShip.SignVertical.Cents = 7500
                const shipping: Objects.Establishment.Price.PrintAndShipBasePrice.SignVertical.Cents = 7500
                if (quantity === 1) {
                    return singleSignItemPrice(productUnitPrice, shipping)
                }
                return Math.floor((shipping + quantity * productUnitPrice) / quantity)
            }
            case 'SIGN:HORIZONTAL': {
                const productUnitPrice: Objects.Establishment.Price.PrintAndShip.SignHorizontal.Cents = 4500
                const shipping: Objects.Establishment.Price.PrintAndShipBasePrice.SignHorizontal.Cents = 7500
                if (quantity === 1) {
                    return singleSignItemPrice(productUnitPrice, shipping)
                }
                return Math.floor((shipping + quantity * productUnitPrice) / quantity)
            }
            case 'SIGN:YARD': {
                const productUnitPrice: Objects.Establishment.Price.PrintAndShip.SignYard.Cents = 1400
                const shipping: Objects.Establishment.Price.PrintAndShipBasePrice.SignYard.Cents = 4000
                if (quantity === 1) {
                    return singleSignItemPrice(productUnitPrice, shipping)
                }
                return Math.floor((shipping + quantity * productUnitPrice) / quantity)
            }
        }

        assertUnreachable(code)
    }

    function singleSignItemPrice(productUnitPrice: number, shipping: number) {
        const priceInCents = shipping + 2 * productUnitPrice * 0.8
        const priceInNearestDollars = Math.floor(priceInCents / 100)
        return priceInNearestDollars * 100
    }

    function getPrintAndShipGreetingCardsBaseUnitPrice(quantity: number) {
        const constant =
            quantity <= 200
                ? 211
                : quantity <= 500
                  ? 193
                  : quantity <= 1000
                    ? 144
                    : quantity <= 2000
                      ? 142
                      : quantity <= 5000
                        ? 125
                        : quantity <= 10000
                          ? 129
                          : quantity <= 25000
                            ? 114
                            : quantity <= 50000
                              ? 104
                              : 90

        const slope =
            quantity <= 200
                ? -0.201
                : quantity <= 500
                  ? -0.11
                  : quantity <= 1000
                    ? -0.012
                    : quantity <= 2000
                      ? -0.01
                      : quantity <= 5000
                        ? -0.00133
                        : quantity <= 10000
                          ? -0.0022
                          : quantity <= 25000
                            ? -0.000667
                            : quantity <= 50000
                              ? -0.00028
                              : 0

        const unitPrice = Math.round(slope * quantity + constant)
        return unitPrice
    }

    export function getDirectMailGreetingCardsBaseUnitPrice(quantity: number, maxUnitPrice: number | undefined) {
        const constant =
            quantity <= 200
                ? 211
                : quantity <= 500
                  ? 193
                  : quantity <= 1000
                    ? 144
                    : quantity <= 2000
                      ? 142
                      : quantity <= 5000
                        ? 125
                        : quantity <= 10000
                          ? 129
                          : quantity <= 25000
                            ? 114
                            : quantity <= 50000
                              ? 104
                              : 90

        const slope =
            quantity <= 200
                ? -0.201
                : quantity <= 500
                  ? -0.11
                  : quantity <= 1000
                    ? -0.012
                    : quantity <= 2000
                      ? -0.01
                      : quantity <= 5000
                        ? -0.00133
                        : quantity <= 10000
                          ? -0.0022
                          : quantity <= 25000
                            ? -0.000667
                            : quantity <= 50000
                              ? -0.00028
                              : 0

        const extraSurcharge = 20 // We wanted to release with the above copied from Letters + 20c
        const unitPrice = Math.round(slope * quantity + constant + extraSurcharge)
        if (maxUnitPrice !== undefined) {
            return Math.round(Math.min(unitPrice, maxUnitPrice))
        } else {
            return unitPrice
        }
    }

    export function getLettersBaseUnitPrice(quantity: number, maxUnitPrice: number | undefined) {
        const constant =
            quantity <= 200
                ? 211
                : quantity <= 500
                  ? 193
                  : quantity <= 1000
                    ? 144
                    : quantity <= 2000
                      ? 142
                      : quantity <= 5000
                        ? 125
                        : quantity <= 10000
                          ? 129
                          : quantity <= 25000
                            ? 114
                            : quantity <= 50000
                              ? 104
                              : 90

        const slope =
            quantity <= 200
                ? -0.201
                : quantity <= 500
                  ? -0.11
                  : quantity <= 1000
                    ? -0.012
                    : quantity <= 2000
                      ? -0.01
                      : quantity <= 5000
                        ? -0.00133
                        : quantity <= 10000
                          ? -0.0022
                          : quantity <= 25000
                            ? -0.000667
                            : quantity <= 50000
                              ? -0.00028
                              : 0

        const unitPrice = Math.round(slope * quantity + constant)
        if (maxUnitPrice !== undefined) {
            return Math.round(Math.min(unitPrice, maxUnitPrice))
        } else {
            return unitPrice
        }
    }

    export function getPaperSurcharge(paperBasisWeight: 67 | 70 | 100 | 220 | 600 | 650): number {
        const superThickCents: Objects.Establishment.Price.PaperSurcharge.SuperThickCents = 5
        switch (paperBasisWeight) {
            case 67:
                return 0
            case 70:
                return 0
            case 100:
                return 0
            case 220:
                return superThickCents
            case 600:
                return 0
            case 650:
                return 0
        }
    }

    export function getSubmissionOptions(params: {
        isASV: boolean
        isUVCoatingChecked: boolean
        recordsInLists: number
        selectedPaper: 67 | 70 | 100 | 220 | 600 | 650
        selectedPostageClass: Objects.MailingOrder.IFields.PostageClass.Value
        selectedPrintAndShipQuantity: number
        productCode: Objects.Product.Code.Any
        shippingOption: Objects.MailingOrder.IFields.ShippingOption.Any | undefined
        hStakesQuantity: number
        isGrommetHolesIncluded: boolean
    }): Objects.MailingOrder.Submission.Options.Any | undefined {
        const { isASV, isUVCoatingChecked, recordsInLists, selectedPaper, selectedPrintAndShipQuantity, selectedPostageClass, productCode } = params
        if (!productCode) {
            return
        }

        if (Utils.Product.isDisabledProductCode(productCode)) {
            return
        }

        // TODO(products) no TS warning when a new product type or variant is added
        if (productCode === 'LETTER') {
            if (selectedPaper === 70 && selectedPostageClass !== 'PRINT AND SHIP') {
                const opts: Objects.MailingOrder.Submission.Options.DirectMail.Letter = {
                    isASV,
                    paperBasisWeight: selectedPaper,
                    postageClass: selectedPostageClass,
                    printCoating: undefined,
                    quantity: recordsInLists,
                    productCode,
                }
                return opts
            } else {
                return
            }
        } else if (selectedPostageClass === 'PRINT AND SHIP') {
            if (!Utils.Product.isValidPrintAndShipCode(productCode)) {
                return
            }

            if (selectedPaper !== 70) {
                const opts: Objects.MailingOrder.Submission.Options.PrintAndShip = {
                    isASV: false,
                    paperBasisWeight: selectedPaper,
                    postageClass: selectedPostageClass,
                    printCoating: isUVCoatingChecked ? 'UV' : undefined,
                    quantity: selectedPrintAndShipQuantity,
                    productCode,
                    shippingOption: params.shippingOption,
                    hStakeQuantity: params.hStakesQuantity,
                    isGrommetHolesIncluded: params.isGrommetHolesIncluded,
                }
                return opts
            } else {
                return
            }
        } else {
            if (selectedPaper !== 70 && selectedPaper !== 67 && selectedPaper !== 600 && selectedPaper !== 650) {
                if (!Utils.Product.isValidDirectMailCode(productCode)) {
                    return
                }

                const opts: Objects.MailingOrder.Submission.Options.DirectMail.NonLetter = {
                    isASV,
                    paperBasisWeight: selectedPaper,
                    postageClass: selectedPostageClass,
                    printCoating: isUVCoatingChecked ? 'UV' : undefined,
                    quantity: recordsInLists,
                    productCode,
                }

                return opts
            } else {
                return
            }
        }
    }

    export function getPriceBreakdown(
        establishmentPriceMap: Objects.MailingOrder.Submission.PriceMap,
        submissionOptions: Objects.MailingOrder.Submission.Options.Any,
        totalCredits: number,
        discountPercent: number,
        ordersCount: number,
        discount: Objects.PromoCode.Discount.Any | undefined
    ): Objects.MailingOrder.PriceBreakdown {
        let baseUnitPriceCents: number

        const defaultFirstClassSurcharge: Objects.Establishment.Price.FirstClassSurcharge.Cents = 23
        const firstClassSurchargeCents = establishmentPriceMap[submissionOptions.productCode]?.firstClassSurchargeCents ?? defaultFirstClassSurcharge

        switch (submissionOptions.postageClass) {
            case 'PRINT AND SHIP': {
                // const priceMapForProduct = establishmentPriceMap[submissionOptions.productCode]
                // const maxUnitPriceCents = priceMapForProduct && 'maxUnitPriceCents' in priceMapForProduct ? priceMapForProduct.maxUnitPriceCents : undefined

                switch (submissionOptions.productCode) {
                    case 'BROCHURE':
                    case 'BROCHURE:PRINT-AND-SHIP-ONLY':
                    case 'BUSINESS-CARD:HORIZONTAL':
                    case 'BUSINESS-CARD:VERTICAL':
                    case 'DOOR-HANGER':
                    case 'FLYER':
                    case 'GREETING-CARD:HORIZONTAL':
                    case 'GREETING-CARD:VERTICAL':
                    case 'POSTCARD':
                    case 'SIGN:HORIZONTAL':
                    case 'SIGN:RIDER':
                    case 'SIGN:VERTICAL':
                    case 'SIGN:YARD':
                    case 'TRI-FOLD-BROCHURE': {
                        baseUnitPriceCents = MailingOrder.getPrintAndShipBaseUnitPrice(
                            submissionOptions.quantity,
                            submissionOptions.productCode
                            // maxUnitPriceCents
                        )
                        break
                    }
                }

                if (baseUnitPriceCents === undefined) {
                    throw new Error('Not Implemented')
                }

                break
            }
            case 'FIRST':
            case 'STANDARD': {
                switch (submissionOptions.productCode) {
                    case 'BROCHURE':
                    case 'POSTCARD': {
                        baseUnitPriceCents = establishmentPriceMap[submissionOptions.productCode].unitPriceCents
                        break
                    }
                    case 'GREETING-CARD:HORIZONTAL':
                    case 'GREETING-CARD:VERTICAL': {
                        baseUnitPriceCents = getDirectMailGreetingCardsBaseUnitPrice(
                            submissionOptions.quantity,
                            establishmentPriceMap[submissionOptions.productCode].maxUnitPriceCents
                        )
                        break
                    }
                    case 'LETTER': {
                        baseUnitPriceCents = getLettersBaseUnitPrice(
                            submissionOptions.quantity,
                            establishmentPriceMap[submissionOptions.productCode].maxUnitPriceCents
                        )
                        break
                    }
                }
                if (baseUnitPriceCents === undefined) {
                    throw new Error('Not Implemented')
                }

                break
            }
        }

        if (!baseUnitPriceCents) {
            return {
                fixed: {
                    surchargeCentsPerOrder: 0,
                },
                invoice: {
                    centsForAllOrders: 0,
                    centsPerOrder: 0,
                },
                value: {
                    cents: 0,
                    dollars: 0,
                    centsForAllOrders: 0,
                    centsPerOrder: 0,
                    dollarsForAllOrders: 0,
                    dollarsPerOrder: 0,
                },
                unit: {
                    baseCents: baseUnitPriceCents,
                    withSurchargesCents: 0,
                    withSurchargesMinusCoatingAndPaperCents: 0,
                    withSurchargesAndDiscountCents: 0,
                },
                credits: {
                    consumptionForSurchargePerOrder: 0,
                    consumptionTotalPerOrder: 0,
                    numberOfItemsToFundPerOrder: 0,
                },
                numberOfItemsToChargePerOrder: 0,
                discount: {
                    amountCents: 0,
                },
            }
        }

        // TODO Add shared utility function to calculate total surcharges #1176
        // Also use on server
        const firstClassSurcharge = firstClassSurchargeCents
        const asvSurcharge: Objects.Establishment.Price.AutomatedSellerValuationSurcharge.Cents = 5
        const coatingSurcharge = submissionOptions.printCoating === 'UV' ? establishmentPriceMap.OPTIONS.uvCoatingPriceCents : 0

        const paperSurcharge = Utils.MailingOrder.getPaperSurcharge(submissionOptions.paperBasisWeight)
        const asvCost = submissionOptions.isASV ? asvSurcharge : 0
        const classCost = submissionOptions.postageClass === 'FIRST' ? firstClassSurcharge : 0

        const totalUnitSurchagesCents = classCost + asvCost + coatingSurcharge + paperSurcharge
        const totalUnitSurchargesMinusCoatingAndPaperCents = classCost + asvCost

        const unitPriceWithUnitSurchargesCents = baseUnitPriceCents + totalUnitSurchagesCents
        const unitPriceWithUnitSurchargesMinusCoatingAndPaperCents = baseUnitPriceCents + totalUnitSurchargesMinusCoatingAndPaperCents

        const fixedSurchargesCentsPerOrder =
            submissionOptions.postageClass === 'PRINT AND SHIP'
                ? getShippingSurchargeCents(submissionOptions.postageClass, submissionOptions.shippingOption, submissionOptions.productCode)
                : 0

        let totalAdditionalSurchargeCentsPerOrder = fixedSurchargesCentsPerOrder
        let hStakeUnitPriceCentsPerOrder = 0

        if (
            submissionOptions.postageClass === 'PRINT AND SHIP' &&
            Utils.MailingOrder.isHStakesAnOption(submissionOptions.productCode) &&
            submissionOptions.hStakeQuantity > 0
        ) {
            const hStakeBasePriceCents: Objects.Establishment.Price.PrintAndShip.HStake.Cents = 500
            hStakeUnitPriceCentsPerOrder = submissionOptions.hStakeQuantity * hStakeBasePriceCents
            totalAdditionalSurchargeCentsPerOrder += hStakeUnitPriceCentsPerOrder
        }

        if (
            submissionOptions.postageClass === 'PRINT AND SHIP' &&
            Utils.MailingOrder.isGrommetHolesAnOption(submissionOptions.productCode) &&
            submissionOptions.isGrommetHolesIncluded
        ) {
            const grommetHoleBasePriceCents: Objects.Establishment.Price.PrintAndShip.GrommetHole.Cents = 200
            totalAdditionalSurchargeCentsPerOrder += grommetHoleBasePriceCents * submissionOptions.quantity
        }

        const totalAdditionalSurchargeAfterDiscountCents = Math.round((totalAdditionalSurchargeCentsPerOrder * (100 - discountPercent)) / 100)
        const totalAdditionalSurchargeWithDiscountCentsTotal = Math.round(totalAdditionalSurchargeAfterDiscountCents * ordersCount)

        const unitPriceWithSurchargesAndDiscountCents = Math.round((unitPriceWithUnitSurchargesCents * (100 - discountPercent)) / 100)
        const valueCentsPerOrder =
            Math.round(unitPriceWithSurchargesAndDiscountCents * submissionOptions.quantity) + totalAdditionalSurchargeWithDiscountCentsTotal
        const valueCentsForAllOrders = valueCentsPerOrder * ordersCount
        const valueDollarsPerOrder = parseFloat((valueCentsPerOrder / 100).toFixed(2))
        const valueDollarsForAllOrders = parseFloat((valueCentsForAllOrders / 100).toFixed(2))

        const maxNumberOfItemsToFundWithCredits = Math.floor(totalCredits / unitPriceWithSurchargesAndDiscountCents)
        const maxNumberOfItemsToFundWithCreditsPerOrder = Math.floor(maxNumberOfItemsToFundWithCredits / ordersCount)
        const numberOfItemsToFundWithCreditsPerOrder = Math.min(maxNumberOfItemsToFundWithCreditsPerOrder, submissionOptions.quantity)
        const numberOfItemsToChargePerOrder = submissionOptions.quantity - numberOfItemsToFundWithCreditsPerOrder

        let creditsTotalConsumptionPerOrder = numberOfItemsToFundWithCreditsPerOrder * unitPriceWithSurchargesAndDiscountCents
        let creditsTotalConsumptionForAllOrders = creditsTotalConsumptionPerOrder * ordersCount
        let creditsRemainingPerOrder = Math.floor((totalCredits - creditsTotalConsumptionForAllOrders) / ordersCount)
        let invoiceAmountPerOrder = valueCentsPerOrder - creditsTotalConsumptionPerOrder
        let invoiceAmountCentsForAllOrders = invoiceAmountPerOrder * ordersCount
        let creditsConsumptionForSurchargePerOrder = 0
        let discountAmountCents = 0

        if (totalAdditionalSurchargeCentsPerOrder > 0 && creditsRemainingPerOrder > 0) {
            const minimumInvoiceAmount = 50

            if (creditsRemainingPerOrder >= totalAdditionalSurchargeCentsPerOrder) {
                creditsConsumptionForSurchargePerOrder = totalAdditionalSurchargeCentsPerOrder
            } else if (creditsRemainingPerOrder > totalAdditionalSurchargeCentsPerOrder - minimumInvoiceAmount) {
                creditsConsumptionForSurchargePerOrder = totalAdditionalSurchargeCentsPerOrder - minimumInvoiceAmount
            } else {
                creditsConsumptionForSurchargePerOrder = creditsRemainingPerOrder
            }

            creditsRemainingPerOrder -= creditsConsumptionForSurchargePerOrder
            invoiceAmountPerOrder -= creditsConsumptionForSurchargePerOrder
            creditsTotalConsumptionPerOrder += creditsConsumptionForSurchargePerOrder
            creditsTotalConsumptionForAllOrders += creditsConsumptionForSurchargePerOrder * ordersCount
            invoiceAmountCentsForAllOrders -= creditsConsumptionForSurchargePerOrder * ordersCount
        }

        if (discount) {
            discountAmountCents = computeDiscountAmount(invoiceAmountCentsForAllOrders, discount)
            invoiceAmountCentsForAllOrders -= discountAmountCents
            invoiceAmountPerOrder -= Math.round(discountAmountCents / ordersCount)
        }

        return {
            fixed: {
                surchargeCentsPerOrder: fixedSurchargesCentsPerOrder,
            },
            invoice: {
                centsForAllOrders: invoiceAmountCentsForAllOrders,
                centsPerOrder: invoiceAmountPerOrder,
            },
            value: {
                // TODO Remove July 2024
                /**
                 * @deprecated cents should not be used.
                 */
                cents: valueCentsPerOrder,
                /**
                 * @deprecated dollars should not be used.
                 */
                dollars: valueDollarsPerOrder,
                dollarsPerOrder: valueDollarsPerOrder,
                dollarsForAllOrders: valueDollarsForAllOrders,
                centsPerOrder: valueCentsPerOrder,
                centsForAllOrders: valueCentsForAllOrders,
            },
            unit: {
                baseCents: baseUnitPriceCents,
                withSurchargesCents: unitPriceWithUnitSurchargesCents,
                withSurchargesMinusCoatingAndPaperCents: unitPriceWithUnitSurchargesMinusCoatingAndPaperCents,
                withSurchargesAndDiscountCents: unitPriceWithSurchargesAndDiscountCents,
            },
            credits: {
                consumptionForSurchargePerOrder: creditsConsumptionForSurchargePerOrder,
                consumptionTotalPerOrder: creditsTotalConsumptionPerOrder,
                numberOfItemsToFundPerOrder: numberOfItemsToFundWithCreditsPerOrder,
            },
            numberOfItemsToChargePerOrder,
            discount: {
                amountCents: discountAmountCents,
            },
        }
    }

    export function getShippingSurchargeCents(
        postageClass: Objects.MailingOrder.IFields.PostageClass.Value,
        shippingOption: Objects.MailingOrder.IFields.ShippingOption.Any | undefined,
        productCode: Objects.Product.Code.Any
    ) {
        const productType = Utils.Product.getProductTypeAndVariantFromCode(productCode).type

        if (postageClass === 'PRINT AND SHIP') {
            if (productType !== 'SIGN') {
                const shippingOptionStandard: Objects.Establishment.Price.ShippingOption.Standard.Cents = 0
                const shippingOptionTwoDays: Objects.Establishment.Price.ShippingOption.TwoDays.Cents = 900
                const shippingOptionNextMorning: Objects.Establishment.Price.ShippingOption.NextDayMorning.Cents = 8000
                const shippingOptionNextAfternoon: Objects.Establishment.Price.ShippingOption.NextDayAfternoon.Cents = 4500
                const localShippingOptionTwoHours: Objects.Establishment.Price.ShippingOption.LocalTwoHours.Cents = 2000
                const localShippingOptionFourHours: Objects.Establishment.Price.ShippingOption.LocalFourHours.Cents = 1600

                switch (shippingOption) {
                    case 'LOCAL-TWO-HOURS':
                        return localShippingOptionTwoHours
                    case 'LOCAL-FOUR-HOURS':
                        return localShippingOptionFourHours
                    case 'STANDARD':
                        return shippingOptionStandard
                    case 'TWO-DAYS':
                        return shippingOptionTwoDays
                    case 'NEXT-DAY-MORNING':
                        return shippingOptionNextMorning
                    case 'NEXT-DAY-AFTERNOON':
                        return shippingOptionNextAfternoon
                    case 'LOCAL-DELIVERY':
                        return localShippingOptionFourHours
                    case undefined:
                        return 0
                }
                assertUnreachable(shippingOption)
            } else {
                const shippingOptionStandard: Objects.Establishment.Price.SignShippingOption.Standard.Cents = 0
                const shippingOptionTwoDays: Objects.Establishment.Price.SignShippingOption.TwoDays.Cents = 4400
                const shippingOptionNextMorning: Objects.Establishment.Price.SignShippingOption.NextDayMorning.Cents = 12900
                const shippingOptionNextAfternoon: Objects.Establishment.Price.SignShippingOption.NextDayAfternoon.Cents = 8900
                const localShippingOptionTwoHours: Objects.Establishment.Price.SignShippingOption.LocalTwoHours.Cents = 2000
                const localShippingOptionFourHours: Objects.Establishment.Price.SignShippingOption.LocalFourHours.Cents = 1600

                switch (shippingOption) {
                    case 'LOCAL-TWO-HOURS':
                        return localShippingOptionTwoHours
                    case 'LOCAL-FOUR-HOURS':
                        return localShippingOptionFourHours
                    case 'STANDARD':
                        return shippingOptionStandard
                    case 'TWO-DAYS':
                        return shippingOptionTwoDays
                    case 'NEXT-DAY-MORNING':
                        return shippingOptionNextMorning
                    case 'NEXT-DAY-AFTERNOON':
                        return shippingOptionNextAfternoon
                    case 'LOCAL-DELIVERY':
                        return localShippingOptionFourHours
                    case undefined:
                        return 0
                }
                assertUnreachable(shippingOption)
            }
        }
        return 0
    }

    export function getPrintAndShipMinQuantity(productCode: Objects.Product.Code.Any): number {
        let minQuantity: number

        switch (productCode) {
            case 'POSTCARD':
            case 'GREETING-CARD':
            case 'LETTER':
            case 'GREETING-CARD:HORIZONTAL':
            case 'GREETING-CARD:VERTICAL':
                minQuantity = 100
                break
            case 'BROCHURE':
            case 'BROCHURE:PRINT-AND-SHIP-ONLY':
            case 'FLYER':
            case 'BUSINESS-CARD:HORIZONTAL':
            case 'BUSINESS-CARD:VERTICAL':
            case 'DOOR-HANGER':
            case 'TRI-FOLD-BROCHURE':
                minQuantity = 50
                break
            case 'SIGN:HORIZONTAL':
            case 'SIGN:VERTICAL':
            case 'SIGN:YARD':
            case 'SIGN:RIDER':
                minQuantity = 1
                break
        }
        return minQuantity
    }

    export function getPrintAndShipDefaultQuantity(productCode: Objects.Product.Code.Any | undefined): number {
        let defaultQuantity: number

        switch (productCode) {
            case 'BROCHURE':
            case 'POSTCARD':
            case 'GREETING-CARD':
            case 'LETTER':
            case 'GREETING-CARD:HORIZONTAL':
            case 'GREETING-CARD:VERTICAL':
                defaultQuantity = 250
                break
            case 'FLYER':
            case 'BROCHURE:PRINT-AND-SHIP-ONLY':
            case 'DOOR-HANGER':
            case 'TRI-FOLD-BROCHURE':
                defaultQuantity = 50
                break

            case 'BUSINESS-CARD:HORIZONTAL':
            case 'BUSINESS-CARD:VERTICAL':
                defaultQuantity = 500
                break
            case 'SIGN:HORIZONTAL':
            case 'SIGN:VERTICAL':
            case 'SIGN:YARD':
            case 'SIGN:RIDER':
                defaultQuantity = 5
                break
            case undefined:
                defaultQuantity = 250
        }
        return defaultQuantity
    }

    export function isHStakesAnOption(productCode: Objects.Product.Code.Any | undefined): boolean {
        if (!productCode) {
            return false
        }

        switch (productCode) {
            case 'SIGN:YARD':
                return true
            case 'BROCHURE':
            case 'BROCHURE:PRINT-AND-SHIP-ONLY':
            case 'BUSINESS-CARD:HORIZONTAL':
            case 'BUSINESS-CARD:VERTICAL':
            case 'DOOR-HANGER':
            case 'FLYER':
            case 'GREETING-CARD':
            case 'LETTER':
            case 'POSTCARD':
            case 'TRI-FOLD-BROCHURE':
            case 'SIGN:HORIZONTAL':
            case 'SIGN:VERTICAL':
            case 'SIGN:RIDER':
            case 'GREETING-CARD:HORIZONTAL':
            case 'GREETING-CARD:VERTICAL':
                return false
        }
    }

    export function isGrommetHolesAnOption(productCode: Objects.Product.Code.Any | undefined): boolean {
        if (!productCode) {
            return false
        }

        switch (productCode) {
            case 'SIGN:YARD':
                return true
            case 'BROCHURE':
            case 'BROCHURE:PRINT-AND-SHIP-ONLY':
            case 'BUSINESS-CARD:HORIZONTAL':
            case 'BUSINESS-CARD:VERTICAL':
            case 'DOOR-HANGER':
            case 'FLYER':
            case 'GREETING-CARD':
            case 'LETTER':
            case 'POSTCARD':
            case 'TRI-FOLD-BROCHURE':
            case 'SIGN:HORIZONTAL':
            case 'SIGN:VERTICAL':
            case 'SIGN:RIDER':
            case 'GREETING-CARD:HORIZONTAL':
            case 'GREETING-CARD:VERTICAL':
                return false
        }
    }

    export function isReturnAddressRequired(productCode: Objects.Product.Code.Any | undefined, postageClass: Objects.MailingOrder.IFields.PostageClass.Value) {
        let isDirectMail: boolean
        switch (postageClass) {
            case 'FIRST':
            case 'STANDARD':
                isDirectMail = true
                break
            case 'PRINT AND SHIP':
                isDirectMail = false
                break
        }

        if (!isDirectMail) {
            return false
        }

        switch (productCode) {
            case 'GREETING-CARD':
            case 'GREETING-CARD:HORIZONTAL':
            case 'GREETING-CARD:VERTICAL':
            case 'LETTER':
                return true
            case 'BROCHURE':
            case 'BROCHURE:PRINT-AND-SHIP-ONLY':
            case 'BUSINESS-CARD:HORIZONTAL':
            case 'BUSINESS-CARD:VERTICAL':
            case 'DOOR-HANGER':
            case 'FLYER':
            case 'POSTCARD':
            case 'TRI-FOLD-BROCHURE':
            case 'SIGN:HORIZONTAL':
            case 'SIGN:VERTICAL':
            case 'SIGN:RIDER':
            case 'SIGN:YARD':
            case undefined:
                return false
        }

        assertUnreachable(productCode)
        return false
    }

    /**
     * Computes the final amount after applying a discount code
     * @param originalAmountCents - Original amount in cents
     * @param discount - The discount code to apply
     * @returns number - Final amount in cents
     * @throws Error if discount code is invalid or expired or amount is invalid
     */
    export function computeDiscountAmount(originalAmountCents: number, discount: Objects.PromoCode.Discount.Any): number {
        const discountType = discount.type
        let discountAmountCents: number

        switch (discountType) {
            case 'fixed': {
                discountAmountCents = Math.min(discount.value, originalAmountCents)
                break
            }
            case 'percentage': {
                discountAmountCents = Math.round((originalAmountCents * discount.value) / 100)
                break
            }
            default: {
                assertUnreachable(discountType)
                return 0
            }
        }

        const remainingAmount = originalAmountCents - discountAmountCents
        if (remainingAmount > 0 && remainingAmount < 50) {
            discountAmountCents = Math.max(0, originalAmountCents - 50)
        }

        return discountAmountCents
    }
}
